// frontend/user/src/_lib/upload-run.ts
//
// 统一上传接线:把一个 File 跑完整 uploadFile 流程,并把进度→EMA 速度→store 更新串起来。
// 新上传(use-file-upload)、失败重试 / 中断重选续传(transfers)共用,避免接线重复。
// 设计来源:docs/superpowers/plans/2026-06-07-transfers-persist-and-speed.md 任务 4/5。
//
// V1.2(2026-06-07):暂停 / 恢复 / 全部暂停。本模块持每个进行中任务的 AbortController,
// pauseUpload 中断在传分片 + 置 paused;resumeUpload 用内存 File 重跑 → 命中后端 bysha
// 断点续传(/big/init resumed + HEAD Upload-Parts-Count)。详 specs §12。

import { uploadFile, UploadAbortError } from './upload'
import { useUploadStore } from './upload-store'
import { nextSpeedBps, type SpeedTick } from './upload-speed'

export type RunUploadOptions = {
  parentId?: string
  onUploaded?: () => void
}

// 每个进行中任务一个 AbortController。pauseUpload/pauseAll abort,resumeUpload 重建。
const controllers = new Map<string, AbortController>()

// 并发上限:同时最多 3 个上传在跑(2026-06-24 owner)。多选/续传/重试统一入队,全局生效。
const MAX_CONCURRENT_UPLOADS = 3
type QueueItem = { taskId: string; file: File; opts: RunUploadOptions }
const queue: QueueItem[] = []

// runUpload 现为入队入口:压队列后尝试 pump。真正起跑见 startUpload。
// 调用方先 addTask / resetForRetry 建好任务行(状态 pending),再 runUpload 入队。
export function runUpload(taskId: string, file: File, opts: RunUploadOptions = {}): void {
  queue.push({ taskId, file, opts })
  pump()
}

// pump 在有空闲槽位(controllers.size < 3)时,从队列取下一个仍可跑的任务起跑。
// 守卫:跳过已被取消/完成/出错/中断的任务(done/error/interrupted 及任务行已被移除),
// 这些是在排队等待期间被结清的,不该再起跑;放行 pending/uploading/paused
// (resume/retry 会先把状态置回 pending;paused 直传 runUpload 见于显式恢复语义)。
const NON_RUNNABLE: ReadonlySet<string> = new Set(['done', 'error', 'interrupted'])
function pump(): void {
  while (controllers.size < MAX_CONCURRENT_UPLOADS && queue.length > 0) {
    const next = queue.shift()!
    const t = useUploadStore.getState().tasks.find((x) => x.id === next.taskId)
    if (!t || NON_RUNNABLE.has(t.status)) continue
    startUpload(next.taskId, next.file, next.opts)
  }
}

// startUpload 用已存在的 taskId 跑上传(由 pump 调度,非直接对外)。
function startUpload(taskId: string, file: File, opts: RunUploadOptions = {}): void {
  // 幂等闸:同一 taskId 已有在跑 controller 则不重复起跑(防重复 runUpload 双起跑脚枪)。
  if (controllers.has(taskId)) return
  const { updateTask } = useUploadStore.getState()
  const controller = new AbortController()
  controllers.set(taskId, controller)
  let tick: SpeedTick | null = null

  // 仅当 map 里仍是本轮 controller 时才清理,避免 resume 后旧回调误删新一轮。
  const clearIfCurrent = () => {
    if (controllers.get(taskId) === controller) controllers.delete(taskId)
  }

  // 复用上轮算出的 sha(暂停后 resume):避免 uploadLarge 重算整个文件 hash 卡顿 + init 延迟。
  const cachedSha = useUploadStore.getState().tasks.find((t) => t.id === taskId)?.sha

  // uploadFile 暂停时以 UploadAbortError 解栈;状态已由 onError(signal.aborted 早退)/pauseUpload
  // 处理完毕,这里 .catch 兜住该 reject,消除 console 的 "Uncaught (in promise) UploadAbortError"。
  // 非 abort 的错误同样已经过 onError 上报终态,这里静默吞掉返回的 reject 即可。
  void uploadFile(file, {
    ...(opts.parentId ? { parentId: opts.parentId } : {}),
    ...(cachedSha ? { precomputedSha: cachedSha } : {}),
    signal: controller.signal,
    onShaComputed: (sha) => {
      // 首次现算出 sha → 落 store,供本任务后续 resume 透传复用。
      updateTask(taskId, { sha })
    },
    onPhase: (phase) => {
      // 子阶段边界(preparing/transferring/finalizing):提为 active 并记 phase。
      // 高频 onProgress 不带 phase,合并 patch 会保留此处设定的 phase。
      updateTask(taskId, { status: 'uploading', phase })
    },
    onHashProgress: (pct) => {
      // phase 已由 onPhase('preparing') 置位;此处只更新瞬时 hashPercent(跳 IDB 高频写)。
      updateTask(taskId, { hashPercent: pct })
    },
    onProgress: (p) => {
      tick = nextSpeedBps(tick, p.bytesUploaded, Date.now())
      updateTask(taskId, {
        bytesUploaded: p.bytesUploaded,
        percent: p.percent,
        status: 'uploading',
        speedBps: tick.ema,
      })
    },
    onSuccess: () => {
      clearIfCurrent()
      updateTask(taskId, { percent: 100, bytesUploaded: file.size, status: 'done', speedBps: undefined, phase: undefined })
      opts.onUploaded?.()
      pump()
    },
    onError: (err) => {
      clearIfCurrent()
      // 暂停触发的 abort 不是失败:状态已由 pauseUpload 置 paused,这里不覆盖。
      if (controller.signal.aborted) {
        pump()
        return
      }
      updateTask(taskId, { status: 'error', error: err.message, speedBps: undefined, phase: undefined })
      pump()
    },
  }).then(
    () => settle(),
    (err: unknown) => {
      // uploadFile 的 reject 已在 onError 处理过终态;此处兜底吞掉(尤其 UploadAbortError),
      // 避免成为未处理的 promise rejection 刷进 console。
      // 非 abort 错误理论上都先经 onError;此处仅做槽位释放兜底,不重复上报。
      void (err instanceof UploadAbortError)
      settle()
    },
  )

  // settle:上传 promise 落定即释放槽位 + 补下一个排队任务。clearIfCurrent 幂等
  // (onSuccess/onError 已清过则 no-op);以 promise settle 为准,确保槽位必被释放。
  function settle(): void {
    clearIfCurrent()
    pump()
  }
}

// resetForRetryAndRun 把任务行重置为 pending(清进度/错误)后用给定 File 重跑。
// 失败重试(原 File 仍在)与中断重选(用户重选同一文件 → 命中后端 bysha 续传)共用。
export function resetForRetryAndRun(taskId: string, file: File, opts: RunUploadOptions = {}): void {
  useUploadStore.getState().resetForRetry(taskId)
  runUpload(taskId, file, opts)
}

// pauseUpload 暂停单个任务:置 paused + 清速度,abort 在传分片(uploadFile 停止分片循环)。
export function pauseUpload(taskId: string): void {
  const c = controllers.get(taskId)
  controllers.delete(taskId)
  useUploadStore.getState().updateTask(taskId, { status: 'paused', speedBps: undefined, phase: undefined })
  c?.abort()
  // 暂停腾出一个槽位:从队列补下一个排队任务。
  pump()
}

// resumeUpload 恢复单个任务:用内存中的 File 重跑(命中后端 bysha 从断点续传)。
// 不重置 bytesUploaded/percent —— uploadLarge 续传路径会用 HEAD 校正起点。
// File 丢失(刷新后)→ 无法自动续 → no-op(UI 应改走「已中断」重选)。
export function resumeUpload(taskId: string, opts: RunUploadOptions = {}): void {
  const task = useUploadStore.getState().tasks.find((t) => t.id === taskId)
  if (!task?.file) return
  useUploadStore.getState().updateTask(taskId, { status: 'pending', speedBps: undefined, error: undefined })
  runUpload(taskId, task.file, opts)
}

// pauseAll 暂停所有进行中任务(statsBar 全部暂停按钮)。
export function pauseAll(): void {
  // 先清空等待队列:否则 pauseUpload 末尾的 pump() 会把排队的 pending 任务泵成运行态,
  // 这些新起跑的任务不在循环前的 controllers 快照里 → 反而开始上传。被清出队列的任务
  // 仍是 store 里的 pending 行,用户可手动 resume 重新入队,符合「全部暂停」直觉。
  queue.length = 0
  for (const id of [...controllers.keys()]) pauseUpload(id)
}

// 仅供测试:重置模块级调度状态(controllers / queue),避免跨用例残留串台。
// 生产代码不应调用。
export function __resetUploadSchedulerForTest(): void {
  controllers.clear()
  queue.length = 0
}
